import { useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { bizminer_admin_organization_users_path } from "@/routes";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
}

const ROLES = [
  { value: "viewer", label: "Viewer", description: "View-only access" },
  { value: "member", label: "Member", description: "Standard access" },
  { value: "admin", label: "Admin", description: "Full access" },
];

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export default function CreateUserDialog({
  open,
  onOpenChange,
  organizationId,
}: Props) {
  const { data, setData, post, processing, errors, reset } = useForm({
    user: {
      name: "",
      email_address: "",
      role: "viewer",
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(bizminer_admin_organization_users_path(organizationId), {
      preserveScroll: true,
      onSuccess: () => {
        reset();
        onOpenChange(false);
      },
    });
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Create a new user. A password will be generated automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <FormField label="Name" error={errors["user.name"]}>
              <Input
                value={data.user.name}
                onChange={(e) =>
                  setData("user", { ...data.user, name: e.target.value })
                }
                placeholder="John Doe"
              />
            </FormField>

            <FormField label="Email" error={errors["user.email_address"]}>
              <Input
                type="email"
                value={data.user.email_address}
                onChange={(e) =>
                  setData("user", {
                    ...data.user,
                    email_address: e.target.value,
                  })
                }
                placeholder="john@example.com"
              />
            </FormField>

            <FormField label="Role" error={errors["user.role"]}>
              <Select
                value={data.user.role}
                onValueChange={(value) =>
                  setData("user", { ...data.user, role: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
